import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
    Download,
    ChevronDown,
    ExternalLink,
    FileJson,
    FileCode,
    FileText,
    BookOpen,
    Plug,
} from 'lucide-react';

const API_BASE = 'https://api.satutim.id';

interface Props {
    onMcpClick: () => void;
}

interface ItemSpec {
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    href?: string;
    download?: boolean;
    onSelect?: () => void;
}

/**
 * Resources dropdown — surface the AI-agent friendly artifacts (llms.txt,
 * llms-full.txt, OpenAPI JSON/YAML) plus the MCP setup modal.
 *
 * All resource items are direct links that open in a new tab; the OpenAPI YAML
 * one uses the `download` attribute so browsers prompt a Save dialog instead
 * of rendering it inline.
 */
export default function ResourcesMenu({ onMcpClick }: Props) {
    const items: Array<ItemSpec | 'separator'> = [
        {
            label: 'OpenAPI (JSON)',
            description: 'Spec lengkap, machine-readable',
            icon: FileJson,
            href: `${API_BASE}/openapi.json`,
        },
        {
            label: 'OpenAPI (YAML)',
            description: 'Versi YAML — cocok untuk codegen',
            icon: FileCode,
            href: `${API_BASE}/openapi.yaml`,
            download: true,
        },
        'separator',
        {
            label: 'llms.txt',
            description: 'Index untuk AI agents',
            icon: FileText,
            href: `${API_BASE}/llms.txt`,
        },
        {
            label: 'llms-full.txt',
            description: 'Reference comprehensive untuk AI',
            icon: BookOpen,
            href: `${API_BASE}/llms-full.txt`,
        },
        'separator',
        {
            label: 'MCP setup',
            description: 'Hubungkan ke Claude / Cursor',
            icon: Plug,
            onSelect: onMcpClick,
        },
    ];

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md text-muted hover:text-ink transition-colors"
                    style={{ backgroundColor: 'var(--surface-1)', border: '1px solid var(--border)' }}
                    aria-label="Open resources menu"
                >
                    <Download className="h-3.5 w-3.5" />
                    <span>Resources</span>
                    <ChevronDown className="h-3 w-3 opacity-70" />
                </button>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="end"
                    sideOffset={6}
                    className="z-50 min-w-[260px] rounded-lg p-1 shadow-xl"
                    style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)' }}
                >
                    {items.map((item, idx) => {
                        if (item === 'separator') {
                            return (
                                <DropdownMenu.Separator
                                    key={`sep-${idx}`}
                                    className="my-1 h-px"
                                    style={{ backgroundColor: 'var(--border)' }}
                                />
                            );
                        }
                        const Icon = item.icon;
                        const isLink = !!item.href;
                        const isExternal = isLink && !item.download;
                        const commonClass =
                            'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs text-ink outline-none cursor-pointer data-[highlighted]:bg-bg-hover transition-colors';

                        if (isLink) {
                            return (
                                <DropdownMenu.Item
                                    key={item.label}
                                    asChild
                                    className={commonClass}
                                    style={{ color: 'var(--ink)' }}
                                >
                                    <a
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download={item.download ? '' : undefined}
                                    >
                                        <Icon className="h-3.5 w-3.5 text-muted shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium leading-tight">{item.label}</div>
                                            <div className="text-[10px] text-muted leading-tight mt-0.5">
                                                {item.description}
                                            </div>
                                        </div>
                                        {isExternal && (
                                            <ExternalLink className="h-3 w-3 text-ghost shrink-0" />
                                        )}
                                        {item.download && (
                                            <Download className="h-3 w-3 text-ghost shrink-0" />
                                        )}
                                    </a>
                                </DropdownMenu.Item>
                            );
                        }

                        return (
                            <DropdownMenu.Item
                                key={item.label}
                                onSelect={() => item.onSelect?.()}
                                className={commonClass}
                                style={{ color: 'var(--ink)' }}
                            >
                                <Icon className="h-3.5 w-3.5 text-muted shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium leading-tight">{item.label}</div>
                                    <div className="text-[10px] text-muted leading-tight mt-0.5">
                                        {item.description}
                                    </div>
                                </div>
                            </DropdownMenu.Item>
                        );
                    })}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
